import { useState } from "react";

import { RefreshCw, CheckCircle, AlertCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

interface FailedAttempt {
  id: string;
  created_at: string;
  user_id: string;
  board_type: string;
  step: string;
  error_message: string | null;
  board_id: string | null;
  template_board_id: string | null;
}

const BoardCreationRecovery = () => {
  const [failures, setFailures] = useState<FailedAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const { execute: invokeEdgeFunction } = useEdgeFunction({ auth: true });

  const fetchRecentFailures = async () => {
    setLoading(true);
    try {
      // Get failures from last 7 days that might be recoverable
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data, error } = await supabase
        .from("board_creation_attempts")
        .select("*")
        .eq("status", "failure")
        .gte("created_at", sevenDaysAgo.toISOString())
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      // Filter out attempts that have been successfully retried
      const recoverableFailures = [];
      for (const failure of data || []) {
        // Check if there's a successful attempt after this failure
        const { data: successAttempts } = await supabase
          .from("board_creation_attempts")
          .select("id")
          .eq("user_id", failure.user_id)
          .eq("status", "success")
          .gte("created_at", failure.created_at)
          .limit(1);

        if (!successAttempts || successAttempts.length === 0) {
          recoverableFailures.push(failure);
        }
      }

      setFailures(recoverableFailures);
    } catch (error) {
      console.error("Error fetching failures:", error);
      toast.error("Failed to load recovery attempts");
    } finally {
      setLoading(false);
    }
  };

  const retryCreation = async (attemptId: string) => {
    setRetrying(attemptId);
    try {
      const { data, error } = await invokeEdgeFunction("retry-board-creation", {
        body: { attemptId },
        showErrorToast: true,
      });

      if (error) {
        throw error;
      }

      toast.success(`Board creation recovered from step: ${data.recoveredStep}`);
      
      // Refresh the list
      await fetchRecentFailures();
    } catch (error) {
      console.error("Error retrying board creation:", error);
      // Error toast already shown by hook if showErrorToast is true
    } finally {
      setRetrying(null);
    }
  };

  const getStepLabel = (step: string) => {
    const labels: Record<string, string> = {
      workspace_fetch: "Workspace Fetch",
      board_create: "Board Creation",
      personas_create: "Personas Creation",
      project_create: "Project Creation",
      session_create: "Session Creation",
    };
    return labels[step] || step;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Board Creation Recovery</CardTitle>
            <CardDescription>
              Retry failed board creations from the last 7 days
            </CardDescription>
          </div>
          <Button
            onClick={fetchRecentFailures}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {failures.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No recoverable failures found. Click refresh to check for new failures.
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Failed Step</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((failure) => (
                  <TableRow key={failure.id}>
                    <TableCell className="text-sm">
                      {new Date(failure.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{failure.board_type}</Badge>
                    </TableCell>
                    <TableCell>{getStepLabel(failure.step)}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {failure.error_message || "Unknown error"}
                    </TableCell>
                    <TableCell>
                      {failure.board_id ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Partial
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        onClick={() => retryCreation(failure.id)}
                        disabled={retrying === failure.id}
                        size="sm"
                      >
                        {retrying === failure.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BoardCreationRecovery;
